import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import type { OnFarmImplantSessionEmbryo } from '@/services/on-farm-implant';

import { embryoStatusColors, statusColors } from '../FrozenTab/columnDefs';

const formatNumber = (params: { value?: number | null }) =>
  params.value == null ? '\u2014' : String(params.value);

export const useClientOnFarmEmbryoColumnDefs = (
  readOnly: boolean,
): ColDef<OnFarmImplantSessionEmbryo>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        colId: 'checkbox',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: !readOnly,
        headerCheckboxSelection: !readOnly,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
      },
      {
        colId: 'sourceType',
        headerName: t('onFarmImplant.implantQueue.sourceType'),
        flex: 1,
        minWidth: 150,
        filter: 'agSetColumnFilter',
        valueGetter: () =>
          t('onFarmImplant.implantQueue.sourceTypes.clientOnFarm'),
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return (
            <StatusBadge
              label={params.value}
              colorMap={{
                [params.value]: { bg: 'bg-teal-100', text: 'text-teal-700' },
              }}
            />
          );
        },
      },
      {
        field: 'embryoName',
        headerName: t('onFarmImplant.clientOnFarm.embryo'),
        flex: 1.4,
        minWidth: 150,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          return (
            <div>
              <div className="font-medium text-gray-900">
                {params.data.embryoName ?? '\u2014'}
              </div>
              <div className="text-xs text-gray-500">
                {params.data.recordName ?? ''}
              </div>
            </div>
          );
        },
      },
      {
        field: 'cane',
        headerName: t('onFarmImplant.clientOnFarm.cane'),
        flex: 0.6,
        minWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.clientOnFarm.donor'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.clientOnFarm.sire'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'accountName',
        headerName: t('onFarmImplant.clientOnFarm.account'),
        flex: 1.2,
        minWidth: 130,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'stage',
        headerName: t('onFarmImplant.clientOnFarm.stage'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatNumber,
      },
      {
        field: 'grade',
        headerName: t('onFarmImplant.clientOnFarm.grade'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
        valueFormatter: formatNumber,
      },
      {
        field: 'frozenStrawNumber',
        headerName: t('onFarmImplant.frozen.frozenStraw'),
        flex: 0.7,
        minWidth: 100,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'statusName',
        headerName: t('onFarmImplant.clientOnFarm.workflowStatus'),
        flex: 0.8,
        minWidth: 110,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return <StatusBadge label={params.value} colorMap={statusColors} />;
        },
      },
      {
        field: 'embryoStatusName',
        headerName: t('onFarmImplant.frozen.embryoStatus'),
        flex: 0.9,
        minWidth: 120,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return (
            <StatusBadge label={params.value} colorMap={embryoStatusColors} />
          );
        },
      },
    ],
    [readOnly, t],
  );
};
